import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as modalController from "modalController";
import * as notificationBar from "notificationBar";
import * as premiumUpsellModal from "premiumUpsellModal";
import * as readerPageNavigation from "readerPageNavigation";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { userProfilePath } from "shared/usernameValidation";
import { shareLink, sharesBySheet } from "shareLink";
import * as signupModal from "signupModal";
import ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import * as tts from "tts";
import type { ApplyTranscriptMode } from "views/ApplyTranscriptDialog";
import AudioModal from "views/AudioModal";
import BookReportDialog from "views/BookReportDialog";
import Dialog from "views/Dialog";
import DropdownMenu from "views/DropdownMenu";
import { EditDocument } from "views/EditDocumentView";
import FindInTextModal from "views/FindInTextModal";
import ShareBookView from "views/ShareBookView";
import * as speakingModeControls from "views/speakingModeControls";
import YouTubeModal from "views/YouTubeModal";

interface Props {
  allowYouTube?: boolean;
  appRouter: any;
  hasAudioFile?: boolean;
  isOwner?: boolean;
  onDeleteAudio?: () => void;
  onOpen?: () => void;
  onEditDocumentRefresh?: (options?: { reInitialize?: boolean }) => void;
  onRemoveYouTube?: () => void;
  onSaveYouTubeID?: (userInput: string) => void;
  onSubmitYouTubeTranscript?: (
    transcript: string,
    mode: ApplyTranscriptMode
  ) => void | Promise<void>;
  readerViewStore: ReaderViewStore;
  /**
   * Opens the Settings (formerly AA) tab in the sidebar. Provided by
   * readerPage so the menu doesn't need to know about sidebar plumbing.
   */
  openSettings?: () => void;
}

/** Runs `action` for a signed-in user, and asks a guest to sign in instead. */
const whenSignedIn = async (heading: string, action: () => unknown) => {
  if (await readlangClient.isLocalMode()) {
    signupModal.show({ heading, stores });
    return;
  }
  await action();
};

class ReaderPageDropdownMenuView extends preact.Component<Props> {
  disposeBookEffect?: () => void;
  disposeSpeakingModeEffect?: () => void;
  disposeTtsAvailabilityEffect?: () => void;

  componentDidMount() {
    this.disposeBookEffect = effect(() => {
      void stores.bookModel?.book.value.userBook;
      void stores.bookModel?.book.value.language;
      // Re-render when the text's owner is blocked or unblocked
      void stores.user.blockedUsers.value;
      this.forceUpdate();
    });
    // Re-render when Auto-Pronounce toggles from elsewhere (e.g. the
    // Settings tab) so the menu's checkmark stays in sync.
    this.disposeSpeakingModeEffect = effect(() => {
      void stores.user.state.value.speakingMode;
      this.forceUpdate();
    });
    this.disposeTtsAvailabilityEffect = effect(() => {
      const bookLanguage = stores.bookModel?.book.value.language ?? "";
      void tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language: bookLanguage,
      }).value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeBookEffect?.();
    this.disposeSpeakingModeEffect?.();
    this.disposeTtsAvailabilityEffect?.();
  }

  render() {
    const bookLanguage = stores.bookModel?.book.value.language ?? "";
    const languageSupported =
      tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language: bookLanguage,
      }).value !== "unsupported";
    const languageName = languageMap.map[bookLanguage]?.english ?? bookLanguage;
    const book = stores.bookModel?.book.value;
    const isOthersPublicText =
      book?.public && book.owner?._id !== stores.user.id.value;
    // Someone else who shared this public text, whom the reader may block
    const owner = isOthersPublicText ? book.owner : undefined;
    const blockableOwner =
      owner && !stores.user.isBlocked(owner._id) ? owner : undefined;
    return (
      <DropdownMenu
        onOpen={this.props.onOpen}
        items={[
          ...(stores.bookModel
            ? [
                {
                  type: "toggle" as const,
                  label: "Auto-Pronounce",
                  initialValue: Boolean(stores.user.state.peek().speakingMode),
                  disabled: !languageSupported,
                  onDisabledClick: () => {
                    alert(
                      `No TTS voices are currently available for ${languageName}`
                    );
                  },
                  onToggle: (enabled) => {
                    speakingModeControls.setSpeakingModeWithVoicePrompt(
                      enabled,
                      bookLanguage
                    );
                  },
                },
                { type: "separator" as const },
              ]
            : []),
          {
            type: "action",
            label: "Go to start",
            onClick: () => {
              readerPageNavigation.goToLocation(
                "from",
                0,
                this.props.appRouter
              );
            },
          },
          {
            type: "action",
            label: "Go to furthest read location",
            disabled: !stores.bookModel?.book.value.userBook?.readTo,
            onClick: () => {
              const readTo = stores.bookModel?.book.value.userBook?.readTo;
              if (readTo === undefined) return;
              readerPageNavigation.goToLocation(
                "to",
                Number(readTo),
                this.props.appRouter
              );
            },
          },
          {
            type: "action",
            label: "Go to specific location...",
            onClick: () => {
              readerPageNavigation.showSpecificLocationModal(
                this.props.appRouter,
                this.props.readerViewStore
              );
            },
          },
          {
            type: "action",
            label: "Reset reading progress",
            onClick: async () => {
              if (
                confirm(
                  "Are you sure you want to reset your reading progress for this text?"
                )
              ) {
                if (!stores.bookModel) return;
                await readlangClient.request(
                  "POST",
                  `/api/book/${stores.bookModel.id}/resetReadingProgress`,
                  {}
                );
                window.location.href = `/library/${stores.bookModel.id}`;
              }
            },
          },
          { type: "separator" },
          {
            type: "action",
            label: "Find in text...",
            onClick: () => {
              if (!stores.user.hasPremiumAccess.value) {
                premiumUpsellModal.show({
                  title: "findInText",
                  heading: "Upgrade to search within texts...",
                  subheading: "Find any word or phrase across the full text.",
                  showTitle: false,
                });
                return;
              }
              let findInTextModal: FindInTextModal | null = null;
              modalController.show(
                <Dialog heading="Find in text" class="findInTextDialog">
                  <FindInTextModal
                    ref={(ref) => {
                      findInTextModal = ref;
                    }}
                    appRouter={this.props.appRouter}
                    readerViewStore={this.props.readerViewStore}
                  />
                </Dialog>,
                {
                  fullScreen: deviceDetect.isSmall(),
                  onShow: () => {
                    findInTextModal?.focusInput();
                  },
                }
              );
            },
          },
          { type: "separator" },
          ...(this.props.isOwner && stores.bookModel
            ? [
                {
                  type: "action" as const,
                  label: "Edit text...",
                  onClick: () => {
                    let editDocument: EditDocument | null = null;
                    modalController.show(
                      <Dialog heading="Edit text">
                        <EditDocument
                          ref={(ref) => {
                            editDocument = ref;
                          }}
                          model={stores.bookModel}
                          notificationBar={notificationBar}
                          onSaved={() => modalController.cancel()}
                          refreshPage={(options) =>
                            this.props.onEditDocumentRefresh?.(options)
                          }
                        />
                      </Dialog>,
                      {
                        fullScreen: deviceDetect.isSmall(),
                        className: "editDocumentModalDialog",
                        confirmCancelCallback: () =>
                          !editDocument?.hasChanges() ||
                          confirm("Discard your unsaved changes to this text?"),
                      }
                    );
                  },
                },
                { type: "separator" as const },
              ]
            : []),
          ...(this.props.isOwner && !this.props.hasAudioFile && stores.bookModel
            ? [
                {
                  type: "action" as const,
                  label: "YouTube video...",
                  onClick: () => {
                    modalController.show(
                      <Dialog heading="YouTube video">
                        <YouTubeModal
                          allowYouTube={this.props.allowYouTube}
                          book={stores.bookModel.book.value}
                          onRemoveYouTube={this.props.onRemoveYouTube}
                          onSaveYouTubeID={this.props.onSaveYouTubeID}
                          onSubmitYouTubeTranscript={
                            this.props.onSubmitYouTubeTranscript
                          }
                        />
                      </Dialog>,
                      {
                        fullScreen: deviceDetect.isSmall(),
                        className: "youTubeModalDialog",
                      }
                    );
                  },
                },
                { type: "separator" as const },
              ]
            : []),
          ...(this.props.isOwner && this.props.hasAudioFile && stores.bookModel
            ? [
                {
                  type: "action" as const,
                  label: "Audio...",
                  onClick: () => {
                    modalController.show(
                      <Dialog heading="Audio">
                        <AudioModal
                          bookID={stores.bookModel.id}
                          onDeleteAudio={this.props.onDeleteAudio}
                        />
                      </Dialog>,
                      {
                        fullScreen: deviceDetect.isSmall(),
                        className: "audioModalDialog",
                      }
                    );
                  },
                },
                { type: "separator" as const },
              ]
            : []),
          ...(stores.bookModel?.isUserTheOwner() ||
          stores.bookModel?.book.value.public
            ? [
                {
                  type: "action" as const,
                  label: "Share...",
                  onClick: () => {
                    // Straight to the share sheet, unless there's the text's
                    // sharing to set
                    if (
                      sharesBySheet() &&
                      stores.bookModel &&
                      !stores.bookModel.isUserTheOwner()
                    ) {
                      void shareLink({
                        title: stores.bookModel.book.value.title ?? "",
                        url: `https://readlang.com/library/${stores.bookModel.id}`,
                      });
                      return;
                    }
                    modalController.show(
                      <Dialog heading="Share this text">
                        <ShareBookView />
                      </Dialog>
                    );
                  },
                },
              ]
            : []),
          ...(owner
            ? [
                {
                  type: "action" as const,
                  label: `Shared by ${owner.username ?? owner.displayName}`,
                  onClick: () =>
                    this.props.appRouter.navigate(
                      userProfilePath(owner.username, owner._id),
                      { trigger: true }
                    ),
                },
              ]
            : []),
          ...(isOthersPublicText
            ? [
                {
                  type: "action" as const,
                  label: "Report this text…",
                  onClick: () =>
                    whenSignedIn("Sign in to report this text", () =>
                      modalController.show(
                        <BookReportDialog
                          bookID={book._id}
                          owner={blockableOwner}
                        />
                      )
                    ),
                },
              ]
            : []),
          ...(this.props.openSettings
            ? [
                { type: "separator" as const },
                {
                  type: "action" as const,
                  label: "Open Settings…",
                  onClick: this.props.openSettings,
                },
              ]
            : []),
        ]}
      />
    );
  }
}

export default ReaderPageDropdownMenuView;
